import { __decorate, __metadata, __awaiter } from 'tslib';
import { PlatformUtil } from '../../utils/platform-utils.js';
import { StateManager } from '../store/state-manager.js';
import { Service } from '../../decorators/class/service.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { lit } from '../../utils/literal.js';
import { EventEmitter } from '../event-emitter/event-emitter.js';
import { $logger } from '../../controllers/logger/logger.js';
import { generateRandomId } from '../../utils/string-utils.js';

const DEFAULT_DRAWER_WIDTH = 280;
/**
 * 抽屉推出模式
 *
 * @public
 */
const DrawerMode = {
    /**
     * 悬浮模式，此模式下抽屉不会影响编辑区位置
     */
    FLOAT: lit('float'),
    /**
     * 推动模式，此模式下抽屉会推动编辑区位移
     */
    PUSH: lit('push'),
};
/**
 * 用于管理抽屉菜单的服务
 *
 * @public
 */
let DrawerService = class DrawerService {
    constructor(store, eventEmitter) {
        this.store = store;
        this.eventEmitter = eventEmitter;
        this.leftDrawerMode = 'push';
        this.rightDrawerMode = 'float';
        this.leftDrawerInCloseAnimation = false;
        this.rightDrawerInCloseAnimation = false;
        /**
         * @internal
         * 打开左侧抽屉动画开始时执行
         */
        this.onBeforeShowLeftDrawer = () => {
            this.updateLeftMargin();
        };
        /**
         * @internal
         * 打开左侧抽屉动画结束时执行
         */
        this.onAfterShowLeftDrawer = () => {
            this.onAfterShowDrawer('left');
        };
        /**
         * @internal
         * 打开右侧抽屉动画结束时执行
         */
        this.onAfterShowRightDrawer = () => {
            this.onAfterShowDrawer('right');
        };
        /**
         * @internal
         * 关闭左侧抽屉动画开始时执行
         */
        this.onBeforeCloseLeftDrawer = () => {
            // 标记抽屉关闭动画开始
            this.leftDrawerInCloseAnimation = true;
            if (this.leftDrawerMode === DrawerMode.PUSH) {
                this.updateLeftMargin();
            }
        };
        /**
         * @internal
         * 关闭左侧抽屉动画结束时执行
         */
        this.onAfterCloseLeftDrawer = () => {
            var _a, _b;
            // close动画可能和open动画重叠,这里需要检查动画状态,防止错误的清空了新打开drawer的配置,造成白屏
            const info$ = this.store.get(BuiltinUiState.LeftDrawerInfo);
            (_b = (_a = info$.value).onAfterCloseAnimation) === null || _b === void 0 ? void 0 : _b.call(_a);
            if (this.leftDrawerInCloseAnimation) {
                info$.next({});
            }
            // 打开动画效果
            if (this.isAnimationDisabled()) {
                this.enableAnimation();
            }
            this.eventEmitter.emit('$leftDrawerClosed');
            // 标记抽屉关闭动画结束
            this.leftDrawerInCloseAnimation = false;
        };
        /**
         * @internal
         * 打开右侧抽屉动画开始时执行
         */
        this.onBeforeShowRightDrawer = () => {
            this.updateRightMargin();
        };
        /**
         * @internal
         * 关闭右侧抽屉动画开始时执行
         */
        this.onBeforeCloseRightDrawer = () => {
            // 标记抽屉关闭动画开始
            this.rightDrawerInCloseAnimation = true;
            this.updateRightMargin();
        };
        /**
         * @internal
         * 关闭右侧抽屉动画结束时执行
         */
        this.onAfterCloseRightDrawer = () => {
            var _a, _b;
            // close动画可能和open动画重叠,这里需要检查动画状态,防止错误的清空了新打开drawer的配置,造成白屏
            const info$ = this.store.get(BuiltinUiState.RightDrawerInfo);
            (_b = (_a = info$.value).onAfterCloseAnimation) === null || _b === void 0 ? void 0 : _b.call(_a);
            if (this.rightDrawerInCloseAnimation) {
                info$.next({});
            }
            // 打开动画效果
            if (this.isAnimationDisabled()) {
                this.enableAnimation();
            }
            this.eventEmitter.emit('$rightDrawerClosed');
            // 标记抽屉关闭动画结束
            this.rightDrawerInCloseAnimation = false;
        };
        store.upsert(BuiltinUiState.DisableDrawerAnimation, false);
        store.upsert(BuiltinUiState.LeftDrawerInfo, {});
        store.upsert(BuiltinUiState.RightDrawerInfo, {});
        store.upsert(BuiltinUiState.LeftSubDrawerInfo, []);
        store.upsert(BuiltinUiState.RightSubDrawerInfo, []);
        store.upsert(BuiltinUiState.LeftDrawerPersistContent, []);
        store.upsert(BuiltinUiState.RightDrawerPersistContent, []);
        store.upsert(BuiltinUiState.LeftDrawerHiddenContent, new Map());
        store.upsert(BuiltinUiState.RightDrawerHiddenContent, new Map());
        store.upsert(BuiltinUiState.LeftDrawerOpened, false);
        store.upsert(BuiltinUiState.RightDrawerOpened, false);
        store.upsert(BuiltinUiState.LeftDrawerStyle, {});
        store.upsert(BuiltinUiState.RightDrawerStyle, {});
    }
    /**
     * 左侧抽屉是否打开
     *
     * @param name - drawer 名称
     */
    isLeftOpening(name) {
        if (this.isSubDrawer('left', { name })) {
            return true;
        }
        return this.isOpening('left', name);
    }
    /**
     * 右侧抽屉是否打开
     *
     * @param name - drawer 名称
     */
    isRightOpening(name) {
        if (this.isSubDrawer('right', { name })) {
            return true;
        }
        return this.isOpening('right', name);
    }
    /**
     * 是否打开
     * @param orientation - 方向
     * @param name - drawer名称
     * @internal
     */
    isOpening(orientation, name) {
        var _a;
        const infoKey = orientation === 'left' ? BuiltinUiState.LeftDrawerInfo : BuiltinUiState.RightDrawerInfo;
        const openStatusKey = orientation === 'left' ? BuiltinUiState.LeftDrawerOpened : BuiltinUiState.RightDrawerOpened;
        const opened = (_a = this.store.getValue(openStatusKey)) !== null && _a !== void 0 ? _a : false;
        if (!opened)
            return false;
        if (!name)
            return true;
        const info$ = this.store.get(infoKey);
        return info$.value.name === name;
    }
    updateDrawerShowState(orientation, config) {
        return __awaiter(this, void 0, void 0, function* () {
            const infoKey = orientation === 'left' ? BuiltinUiState.LeftDrawerInfo : BuiltinUiState.RightDrawerInfo;
            const openStatusKey = orientation === 'left' ? BuiltinUiState.LeftDrawerOpened : BuiltinUiState.RightDrawerOpened;
            const updateStyle = orientation === 'left'
                ? this.updateLeftDrawerStyle.bind(this)
                : this.updateRightDrawerStyle.bind(this);
            const willOpenStatus = orientation === 'left' ? '$leftDrawerWillOpen' : '$rightDrawerWillOpen';
            const currentDrawer = this.store.getValue(infoKey);
            if (currentDrawer === null || currentDrawer === void 0 ? void 0 : currentDrawer.beforeClose) {
                const shouldClose = yield currentDrawer.beforeClose();
                if (!shouldClose) {
                    $logger.info(`Closing drawer ${currentDrawer.name} have been prevent due to \`beforeClose\` hook`);
                    return;
                }
            }
            this.eventEmitter.emit(willOpenStatus, config);
            this.store.update(infoKey, config);
            this.store.update(openStatusKey, true);
            // close动画可能和open动画重叠,这里标记一下close动画的状态为false,表示open动画已经开始
            if (orientation === 'left') {
                this.leftDrawerInCloseAnimation = false;
            }
            else {
                this.rightDrawerInCloseAnimation = false;
            }
            updateStyle({});
        });
    }
    /**
     * 打开左侧抽屉
     * 如果前面的drawer已经打开，且不是同一个，前一个将关闭
     * 如果打开的是相同的drawer，将直接更新，不会重复调用onOpen
     * @param config - 抽屉配置
     */
    showLeftDrawer(config = {}) {
        this.showDrawer('left', config);
    }
    onAfterShowDrawer(orientation) {
        var _a;
        const infoKey = orientation === 'left' ? BuiltinUiState.LeftDrawerInfo : BuiltinUiState.RightDrawerInfo;
        const { onAfterOpenAnimation } = (_a = this.store.getValue(infoKey)) !== null && _a !== void 0 ? _a : {};
        onAfterOpenAnimation === null || onAfterOpenAnimation === void 0 ? void 0 : onAfterOpenAnimation();
    }
    /**
     * 更新左侧抽屉容器样式
     * @param style - 样式
     */
    updateLeftDrawerStyle(style) {
        let platformStyle = {};
        if (PlatformUtil.isIPad()) {
            platformStyle = {
                paddingTop: 0,
                marginTop: 0,
            };
        }
        this.store.update(BuiltinUiState.LeftDrawerStyle, Object.assign(Object.assign({}, style), platformStyle));
        this.updateLeftMargin();
    }
    /**
     * 关闭左侧抽屉
     *
     * @param animate - 是否使用关闭动画，默认使用
     */
    closeLeftDrawer(animate) {
        this.closeDrawer('left', animate);
    }
    /**
     * 隐藏左侧抽屉
     * @param name - drawer的名称
     */
    hideLeftDrawer(name) {
        this.hideDrawer(name, 'left');
    }
    /**
     * 隐藏右侧抽屉
     * @param name - drawer的名称
     */
    hideRightDrawer(name) {
        this.hideDrawer(name, 'right');
    }
    /**
     * 隐藏指定抽屉
     * @param name - drawer的名称
     * @public
     */
    hideDrawer(name, orientation) {
        const isHidingLeft = (orientation === 'left' || orientation === undefined) && this.isLeftOpening(name);
        const isHidingRight = (orientation === 'right' || orientation === undefined) && this.isRightOpening(name);
        if (!isHidingLeft && !isHidingRight) {
            $logger.info('drawerService: nothing to hide here');
            return;
        }
        if (isHidingLeft) {
            this.hide('left', name);
        }
        if (isHidingRight) {
            this.hide('right', name);
        }
    }
    /**
     * 打开右侧抽屉
     * 如果前面的drawer已经打开，且不是同一个，前一个将关闭
     * 如果打开的是相同的drawer，将直接更新，不会重复调用onOpen
     * @param config - 抽屉配置
     * @public
     */
    showRightDrawer(config = {}) {
        this.showDrawer('right', config);
    }
    showDrawer(orientation, config = {}) {
        var _a, _b;
        if (config.childOf && this.isOpening(orientation, config.childOf)) {
            const infoKey = orientation === 'left' ? BuiltinUiState.LeftSubDrawerInfo : BuiltinUiState.RightSubDrawerInfo;
            const drawers = (_a = this.store.getValue(infoKey)) !== null && _a !== void 0 ? _a : [];
            if (!config.name) {
                config.name = generateRandomId();
            }
            drawers.push(config);
            this.store.update(infoKey, [...drawers]);
            return;
        }
        const isHiddenBefore = this.isHidden(orientation, config.name);
        if (isHiddenBefore) {
            const hiddenInfo = this.removeFromHidden(config.name, orientation);
            if (hiddenInfo) {
                this.updateDrawer(orientation, hiddenInfo);
            }
            return;
        }
        const isUpdatePreviousSelf = this.isOpening(orientation, (_b = config.name) !== null && _b !== void 0 ? _b : generateRandomId());
        if (isUpdatePreviousSelf) {
            this.updateCurrentDrawer(orientation, config, config.name);
            return;
        }
        this.updateDrawerShowState(orientation, config);
        this.clearSubDrawers(orientation);
    }
    /**
     * 打开或隐藏左抽屉
     * @param config - 抽屉配置
     * @public
     */
    toggleLeftDrawer(config = {}) {
        this.toggleDrawer('left', config);
    }
    /**
     * 打开或隐藏左抽屉
     * @param config - 抽屉配置
     * @public
     */
    toggleRightDrawer(config = {}) {
        this.toggleDrawer('right', config);
    }
    /**
     * 打开或隐藏drawer
     * @param orientation - 方向
     * @param config - 抽屉配置
     * @public
     */
    toggleDrawer(orientation, config = {}) {
        var _a;
        if (this.isOpening(orientation, (_a = config.name) !== null && _a !== void 0 ? _a : generateRandomId())) {
            this.hide(orientation, config.name);
            return;
        }
        if (this.isHidden(orientation, config.name)) {
            this.showDrawer(orientation, { name: config.name });
            return;
        }
        this.showDrawer(orientation, config);
    }
    isSubDrawer(orientation, drawerInfo) {
        var _a;
        const infoKey = orientation === 'left' ? BuiltinUiState.LeftSubDrawerInfo : BuiltinUiState.RightSubDrawerInfo;
        const drawers = (_a = this.store.getValue(infoKey)) !== null && _a !== void 0 ? _a : [];
        return drawers.some(drawer => drawer.name === drawerInfo.name);
    }
    /**
     * 更新当前打开的drawer
     * @param name - drawer名称
     * @public
     */
    updateCurrentDrawer(orientation, drawerInfo, name) {
        if (this.isSubDrawer(orientation, Object.assign(Object.assign({}, drawerInfo), { name }))) {
            this.updateSubDrawer(orientation, Object.assign({}, drawerInfo), name);
            return;
        }
        if (!this.isOpening(orientation, name !== null && name !== void 0 ? name : generateRandomId())) {
            return;
        }
        this.updateDrawer(orientation, drawerInfo);
    }
    /**
     * 更新drawer
     * @internal
     */
    updateDrawer(orientation, drawerInfo) {
        const newDrawerInfo = Object.assign(Object.assign({}, this.getCurrentDrawer(orientation)), drawerInfo);
        this.updateDrawerShowState(orientation, newDrawerInfo);
    }
    updateSubDrawer(orientation, drawerInfo, name) {
        var _a;
        const infoKey = orientation === 'left' ? BuiltinUiState.LeftSubDrawerInfo : BuiltinUiState.RightSubDrawerInfo;
        const drawers = (_a = this.store.getValue(infoKey)) !== null && _a !== void 0 ? _a : [];
        if (name) {
            const index = drawers.findIndex(drawer => drawer.name === name);
            drawers[index] = Object.assign(Object.assign({}, drawerInfo), { name });
            this.store.update(infoKey, [...drawers]);
        }
    }
    /**
     * 更新右侧抽屉容器样式
     *
     * @param style - 样式
     */
    updateRightDrawerStyle(style) {
        let platformStyle = {};
        if (PlatformUtil.isIPad()) {
            platformStyle = {
                paddingTop: 0,
                marginTop: 0,
            };
        }
        this.store.update(BuiltinUiState.RightDrawerStyle, Object.assign(Object.assign({}, style), platformStyle));
        this.updateRightMargin();
    }
    /**
     * 关闭右侧抽屉
     *
     * @param animate - 是否使用关闭动画，默认使用
     */
    closeRightDrawer(animate) {
        this.closeDrawer('right', animate);
    }
    closeDrawer(orientation, animate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isOpening(orientation))
                return;
            const openStatusKey = orientation === 'left' ? BuiltinUiState.LeftDrawerOpened : BuiltinUiState.RightDrawerOpened;
            const config = this.getCurrentDrawer(orientation);
            if (!config) {
                return;
            }
            if (config.beforeClose) {
                const shouldClose = yield config.beforeClose();
                if (!shouldClose) {
                    $logger.info(`Closing drawer ${config.name} have been prevent due to \`beforeClose\` hook`);
                    return;
                }
            }
            //  临时关闭动画效果
            if (animate === false) {
                this.disableAnimation();
            }
            const isHidden = this.isHidden(orientation, config.name);
            if (isHidden) {
                this.removeFromHidden(config.name, orientation);
            }
            else {
                this.clearSubDrawers(orientation);
            }
            this.store.get(openStatusKey).next(false);
        });
    }
    /**
     * 设置左侧 Drawer 与 EditorZone 的 UI 形式
     * float: EditorZone 不受 Drawer 影响，Drawer 表现为浮在编辑区上方
     * push: EditorZone 受 Drawer 影响，Drawer 表现为推动编辑区
     *
     * @param mode - 抽屉模式
     */
    setLeftDrawerMode(mode) {
        this.leftDrawerMode = mode;
        this.updateLeftMargin();
    }
    addPersistContentToLeftDrawer(content) {
        var _a;
        const contents = (_a = this.store.getValue(BuiltinUiState.LeftDrawerPersistContent)) !== null && _a !== void 0 ? _a : [];
        const newContents = contents.concat([content]);
        this.store.update(BuiltinUiState.LeftDrawerPersistContent, newContents);
    }
    addPersistContentToRightDrawer(content) {
        var _a;
        const contents = (_a = this.store.getValue(BuiltinUiState.RightDrawerPersistContent)) !== null && _a !== void 0 ? _a : [];
        const newContents = contents.concat([content]);
        this.store.update(BuiltinUiState.RightDrawerPersistContent, newContents);
    }
    /**
     * 设置右侧 Drawer 与 EditorZone 的 UI 形式
     * float: EditorZone 不受 Drawer 影响，Drawer 表现为浮在编辑区上方
     * push: EditorZone 受 Drawer 影响，Drawer 表现为推动编辑区
     *
     * @param mode - 抽屉模式
     */
    setRightDrawerMode(mode) {
        this.rightDrawerMode = mode;
        this.updateRightMargin();
    }
    updateLeftMargin() {
        var _a;
        const originalMargin = this.store.getValue(BuiltinUiState.EditorZoneMarginLeft);
        if (this.isLeftOpening()) {
            const drawer = document.getElementById('workbench-drawer-left');
            const drawerWidth = (_a = drawer === null || drawer === void 0 ? void 0 : drawer.offsetWidth) !== null && _a !== void 0 ? _a : 0;
            const margin = this.leftDrawerMode === DrawerMode.PUSH ? (drawerWidth !== null && drawerWidth !== void 0 ? drawerWidth : DEFAULT_DRAWER_WIDTH) : 0;
            this.store.upsert(BuiltinUiState.EditorZoneMarginLeft, margin);
            this.eventEmitter.emit('$editorZoneResize');
        }
        else if (originalMargin && originalMargin !== 0) {
            this.store.upsert(BuiltinUiState.EditorZoneMarginLeft, 0);
            this.eventEmitter.emit('$editorZoneResize');
        }
    }
    updateRightMargin() {
        var _a, _b;
        const originalMargin = this.store.getValue(BuiltinUiState.EditorZoneMarginRight);
        if (this.isRightOpening()) {
            const drawer = document.getElementById('workbench-drawer-right');
            const drawerWidth = (_a = drawer === null || drawer === void 0 ? void 0 : drawer.offsetWidth) !== null && _a !== void 0 ? _a : 0;
            let drawerDistanceRight = 0;
            if (drawer) {
                drawerDistanceRight = parseInt((_b = window.getComputedStyle(drawer)) === null || _b === void 0 ? void 0 : _b.right.replace('px', ''), 10);
            }
            const margin = this.rightDrawerMode === DrawerMode.PUSH ? ((drawerWidth !== null && drawerWidth !== void 0 ? drawerWidth : DEFAULT_DRAWER_WIDTH) + drawerDistanceRight) : 0;
            this.store.upsert(BuiltinUiState.EditorZoneMarginRight, margin);
            this.eventEmitter.emit('$editorZoneResize');
        }
        else if (originalMargin && originalMargin !== 0) {
            this.store.upsert(BuiltinUiState.EditorZoneMarginRight, 0);
            this.eventEmitter.emit('$editorZoneResize');
        }
    }
    /**
     * 关闭抽屉的动画效果
     */
    disableAnimation() {
        this.store.get(BuiltinUiState.DisableDrawerAnimation).next(true);
    }
    /**
     * 打开抽屉的动画效果
     */
    enableAnimation() {
        this.store.get(BuiltinUiState.DisableDrawerAnimation).next(false);
    }
    /**
    * 是否打开抽屉的动画效果
    */
    isAnimationDisabled() {
        var _a;
        return (_a = this.store.getValue(BuiltinUiState.DisableDrawerAnimation)) !== null && _a !== void 0 ? _a : false;
    }
    getCurrentDrawer(orientation) {
        const currentKey = orientation === 'left' ? BuiltinUiState.LeftDrawerInfo : BuiltinUiState.RightDrawerInfo;
        const currentDrawer = this.store.getValue(currentKey);
        return currentDrawer;
    }
    hide(orientation, name) {
        const hideKey = orientation === 'left' ? BuiltinUiState.LeftDrawerHiddenContent : BuiltinUiState.RightDrawerHiddenContent;
        const openKey = orientation === 'left' ? BuiltinUiState.LeftDrawerOpened : BuiltinUiState.RightDrawerOpened;
        const currentKey = orientation === 'left' ? BuiltinUiState.LeftDrawerInfo : BuiltinUiState.RightDrawerInfo;
        const currentDrawer = this.getCurrentDrawer(orientation);
        if (!currentDrawer || name !== (currentDrawer === null || currentDrawer === void 0 ? void 0 : currentDrawer.name)) {
            $logger.warn('drawerService: current drawer mistmatched');
            return;
        }
        if (this.isHidden(orientation, name)) {
            return;
        }
        const info$ = this.store.get(currentKey);
        info$.next({ name: '' });
        const contentsSubject = this.store.get(hideKey);
        contentsSubject.next(new Map(contentsSubject.getValue().set(currentDrawer.name, currentDrawer)));
        this.store.get(openKey).next(false);
    }
    /**
     * 是否隐藏
     * @internal
     */
    isHidden(orientation, name) {
        const hideKey = orientation === 'left' ? BuiltinUiState.LeftDrawerHiddenContent : BuiltinUiState.RightDrawerHiddenContent;
        const contents = this.store.getValue(hideKey);
        return !!(contents === null || contents === void 0 ? void 0 : contents.has(name));
    }
    removeFromHidden(name, orientation) {
        const hideKey = orientation === 'left' ? BuiltinUiState.LeftDrawerHiddenContent : BuiltinUiState.RightDrawerHiddenContent;
        const contentsSubject = this.store.get(hideKey);
        const removedHiddenInfo = contentsSubject.getValue().get(name);
        contentsSubject === null || contentsSubject === void 0 ? void 0 : contentsSubject.getValue().delete(name);
        contentsSubject === null || contentsSubject === void 0 ? void 0 : contentsSubject.next(new Map(contentsSubject.getValue()));
        return removedHiddenInfo;
    }
    /**
     * 关闭左侧子侧边栏 (无动画)
     * @param name - 侧边栏名称，不传则关闭所有
     */
    removeLeftSubDrawer(name) {
        this.removeSubDrawer('left', name);
    }
    /**
     * 关闭右侧子侧边栏 (无动画)
     * @param name - 侧边栏名称，不传则关闭所有
     */
    removeRightSubDrawer(name) {
        this.removeSubDrawer('right', name);
    }
    clearSubDrawers(orientation) {
        const infoKey = orientation === 'left' ? BuiltinUiState.LeftSubDrawerInfo : BuiltinUiState.RightSubDrawerInfo;
        this.store.update(infoKey, []);
    }
    removeSubDrawer(orientation, name) {
        var _a;
        const infoKey = orientation === 'left' ? BuiltinUiState.LeftSubDrawerInfo : BuiltinUiState.RightSubDrawerInfo;
        const drawers = (_a = this.store.getValue(infoKey)) !== null && _a !== void 0 ? _a : [];
        if (name) {
            const idx = drawers.findIndex(d => d.name === name);
            if (idx >= 0) {
                drawers.splice(idx, 1);
                this.store.update(infoKey, [...drawers]);
            }
        }
        else {
            this.clearSubDrawers(orientation);
        }
    }
    /**
     * 获取左侧抽屉的推出模式
     */
    getLeftDrawerMode() {
        return this.leftDrawerMode;
    }
    /**
     * 获取右侧抽屉的推出模式
     */
    getRightDrawerMode() {
        return this.rightDrawerMode;
    }
};
DrawerService = __decorate([
    Service({ overridable: true }),
    __metadata("design:paramtypes", [StateManager, EventEmitter])
], DrawerService);

export { DEFAULT_DRAWER_WIDTH, DrawerMode, DrawerService };
//# sourceMappingURL=drawer-service.js.map
